/**
 * Calendar Service
 *
 * Client-side deadline computation from existing service data.
 * No new API needed — derives dates from getMyServices() response.
 */

import { getStateName } from './servicesService';

import type { ServiceSummary } from './servicesService';

// =============================================================================
// Types
// =============================================================================

export type DeadlineUrgency = 'NORMAL' | 'URGENT' | 'CRITICAL' | 'OVERDUE';
export type DeadlineType = 'renewal' | 'annual_report';

export interface CalendarDeadline {
  id: string;
  title: string;
  date: string; // Local calendar day, YYYY-MM-DD (also the all-day ICS contract)
  type: DeadlineType;
  state: string;
  entityName: string;
  serviceId?: string;
  daysRemaining: number;
  urgency: DeadlineUrgency;
  fee?: number;
}

// =============================================================================
// State Annual Report Rules
// Changes once per legislative session — hardcoded is correct here
// =============================================================================

const STATE_ANNUAL_REPORTS: Record<string, { month: number; day: number; fee: number }> = {
  LA: { month: 6, day: 1, fee: 30 },   // Due June 1
  MS: { month: 4, day: 15, fee: 25 },  // Due April 15
  AL: { month: 4, day: 15, fee: 0 },   // Due April 15
};

// =============================================================================
// Computation
// =============================================================================

function getUrgency(daysRemaining: number): DeadlineUrgency {
  if (daysRemaining < 0) return 'OVERDUE';
  if (daysRemaining <= 7) return 'CRITICAL';
  if (daysRemaining <= 30) return 'URGENT';
  return 'NORMAL';
}

function localCalendarDay(date: Date): string {
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Preserve date-only values; timestamps use the same local day as Services. */
function renewalCalendarDay(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
  const timestamp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/;
  if (!dateOnly.test(value) && !timestamp.test(value)) return null;

  // Date parsing otherwise silently rolls impossible days (e.g. February 30).
  const day = value.slice(0, 10);
  const calendarDate = new Date(`${day}T00:00:00Z`);
  if (!Number.isFinite(calendarDate.getTime()) || calendarDate.toISOString().slice(0, 10) !== day) {
    return null;
  }
  if (dateOnly.test(value)) return day;
  const instant = new Date(value);
  if (!Number.isFinite(instant.getTime())) return null;
  const localDay = localCalendarDay(instant);
  return dateOnly.test(localDay) ? localDay : null;
}

function daysUntil(dateStr: string): number {
  // Compare calendar-day ordinals, not elapsed hours across DST boundaries.
  const today = localCalendarDay(new Date());
  return (Date.parse(`${dateStr}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000;
}

function getNextAnnualReportDate(state: string): string | null {
  const rule = STATE_ANNUAL_REPORTS[state];
  if (!rule) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();
  let date = new Date(year, rule.month - 1, rule.day);

  // If this year's date has passed, use next year
  if (date < today) {
    date = new Date(year + 1, rule.month - 1, rule.day);
  }

  // Manual formatting avoids UTC shift from toISOString()
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Generate deadlines from active services
 */
export function getDeadlinesFromServices(services: ServiceSummary[]): CalendarDeadline[] {
  const deadlines: CalendarDeadline[] = [];
  const seenEntities = new Set<string>();

  for (const service of services) {
    if (service.status !== 'ACTIVE') continue;

    // 1. Service renewal deadline
    const renewalDate = renewalCalendarDay(service.renewalDate);
    if (renewalDate) {
      const days = daysUntil(renewalDate);
      deadlines.push({
        id: `renewal-${service.serviceId}`,
        title: `${getStateName(service.state)} RA Renewal`,
        date: renewalDate,
        type: 'renewal',
        state: service.state,
        entityName: service.entityName,
        serviceId: service.serviceId,
        daysRemaining: days,
        urgency: getUrgency(days),
      });
    }

    // 2. State annual report deadline (one per entity per state)
    const entityKey = `${service.state}-${service.entityName}`;
    if (!seenEntities.has(entityKey)) {
      seenEntities.add(entityKey);
      const reportDate = getNextAnnualReportDate(service.state);
      if (reportDate) {
        const days = daysUntil(reportDate);
        const rule = STATE_ANNUAL_REPORTS[service.state];
        deadlines.push({
          id: `annual-${entityKey}`,
          title: `${getStateName(service.state)} Annual Report`,
          date: reportDate,
          type: 'annual_report',
          state: service.state,
          entityName: service.entityName,
          daysRemaining: days,
          urgency: getUrgency(days),
          fee: rule?.fee,
        });
      }
    }
  }

  // Sort by date ascending
  deadlines.sort((a, b) => a.date.localeCompare(b.date));

  return deadlines;
}
